import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT_SETTINGS } from '@reconcept-platform/env';
import { Apollo } from 'apollo-angular';
import { combineLatest, from, type Observable, of, ReplaySubject } from 'rxjs';
import { map, mapTo, switchMap, take, tap } from 'rxjs/operators';
import { AuthApiService, type AuthTokens } from '../auth.api';
import { AUTH_APP_URL } from '../shared-authentication.types';
import { AUTH_TOKEN_SERVICE } from '../tokens/auth-token-service.token';

@Injectable({
  providedIn: 'root',
})
export class WebSessionService {
  private authApi = inject(AuthApiService);
  private storage = inject(AUTH_TOKEN_SERVICE);
  private authAppUrl = inject(AUTH_APP_URL);
  private apollo = inject(Apollo);
  private portfolioClientName = inject(ENVIRONMENT_SETTINGS).portfolioClientName;

  isLoggedIn$: Observable<boolean>;

  private isLoggedInSubject = new ReplaySubject<boolean>(1);

  constructor() {
    this.isLoggedIn$ = this.isLoggedInSubject.asObservable();
  }

  isLoggedIn() {
    return from(this.storage.hasAuthToken());
  }

  // request access token based on the code received from the login SPA
  requestAndSetAccessToken(code: string) {
    // get the codeVerifier we stored in the previous step (before leaving the application to go to login)
    return from(this.storage.retrieve('code_verifier')).pipe(
      switchMap((codeVerifier) =>
        this.authApi
          .requestAccessToken(codeVerifier, code)
          .pipe(switchMap((tokens) => from(this.setAccessTokens(tokens)))),
      ),
    );
  }

  // tries the stored access token
  // if it fails, tries to get a refreshed token
  // if that fails, returns false and redirects
  // temporary extraQueryParams, need to add redirect param to show notification to user if automatic redirect from v2.
  tryAccessToken() {
    // get access token
    return combineLatest([from(this.storage.retrieveAccessToken()), from(this.storage.retrieveRefreshToken())]).pipe(
      take(1),
      switchMap(([accessToken, refreshToken]) =>
        this.authApi.tryAccessTokenOrRefresh(accessToken, refreshToken).pipe(
          switchMap((tokens: AuthTokens | null) => {
            if (tokens) {
              return from(this.storage.setTokens(tokens)).pipe(mapTo(true));
            }
            return of(!!tokens);
          }),
          switchMap((loggedIn) => this.clearCache(loggedIn)),
          tap((isLoggedIn: boolean) => this.isLoggedInSubject.next(isLoggedIn)),
        ),
      ),
    );
  }

  tryAccessTokenOrRedirect(state?: string, queryParamsForLogin?: any) {
    // try to get valid accesstoken and if this fails, redirect to login
    return this.tryAccessToken().pipe(
      tap((isLoggedIn: boolean) => {
        // if login failed, redirect
        // temporary extraQueryParams, need to add redirect param to show notification to user if automatic redirect from v2.
        if (!isLoggedIn) {
          this.redirectToLogin(state, queryParamsForLogin);
        }
      }),
    );
  }

  async logoutWithoutRedirect() {
    // make sure the app is unlocked
    // this.viewerService.unlockApp();

    // forget tokens
    await this.storage.clearStorage();
  }

  async logout(state?: any) {
    const accessToken = await this.storage.retrieveAccessToken();

    await this.logoutWithoutRedirect();

    // be sure that the modals are actually destroyed
    setTimeout.call(this, () => {
      // unset viewerService
      // this.viewerService.unset();

      // // forget entry route
      // this.entryRoute = null;

      // // track
      this.isLoggedInSubject.next(false);

      this.redirectToLogout(accessToken, state);
    });
  }

  async createStoreAndGetCodeVerifier() {
    const codeVerifier = this.getRandomString();
    // store the code challenge plain text for verification on return
    await this.storage.store('code_verifier', codeVerifier);

    return codeVerifier;
  }

  // redirects the user to the login SPA
  // including the necessary Query parameters to perform authorization after successful login
  async redirectToLogin(state?: string, queryParamsForLogin?: any) {
    const href = await this.getLoginUrl(state, queryParamsForLogin);
    window.location.href = href;
  }

  async getLoginUrl(state?: string, queryParamsForLogin?: any) {
    const codeVerifier = await this.createStoreAndGetCodeVerifier();
    const queryParams = await this.authApi.getQueryParametersAsURLParams(codeVerifier, state, queryParamsForLogin);
    // redirect to the login app
    return `${this.authAppUrl}/authorize?${queryParams}`;
  }

  // redirects the user to the login SPA with /logout which will destroy the session

  setInviteToken(token: string) {
    this.storage.setInviteToken(token);
  }

  async hasInviteToken() {
    return this.storage.hasInviteToken();
  }

  // including the necessary Query parameters to perform authorization after successful (re)login
  private async redirectToLogout(accessToken: string, state?: string) {
    const codeVerifier = this.getRandomString();
    await this.storage.store('code_verifier', codeVerifier);
    const queryParametersAsURLParams = await this.authApi.getQueryParametersAsURLParams(codeVerifier, state);

    // redirect to the logout page
    location.href = `${this.authAppUrl}/logout?${queryParametersAsURLParams}&accessToken=${accessToken}`;
  }

  private getRandomString(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  private async setAccessTokens(tokens: AuthTokens) {
    // store new tokens
    // immediately unset code verifier, no longer needed
    await this.storage.clear('code_verifier');
    await this.storage.setTokens(tokens);
  }

  private clearCache(loggedIn: boolean) {
    // resolved lazily: the named client only has to exist once a session actually starts or ends
    const client = this.apollo.use(this.portfolioClientName).client;
    let cleared: Promise<unknown>;
    if (loggedIn) {
      // resetStore retriggers watched queries — use on login
      cleared = client.resetStore();
    } else {
      client.stop(); // stop in-flight queries before dropping the store
      // clearStore does NOT retrigger watched queries — use on logout
      cleared = client.clearStore();
    }
    return from(cleared).pipe(map(() => loggedIn));
  }
}
